import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Building2, MapPin, Phone, Mail, Globe, ArrowLeft, AlertCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ShareMenu } from '@/components/ShareMenu';
import { getDirectoryBySlug } from '@/lib/api';

const SITE_URL = 'https://paisleyscotland.com';

const DirectoryDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getDirectoryBySlug(slug)
      .then((res) => { if (!cancelled) setBiz(res.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const pageUrl = `${SITE_URL}/directory/${slug}`;
  const pageTitle = biz
    ? `${biz.business_name}${biz.category ? ` — ${biz.category}` : ''} | Paisley Business Directory`
    : 'Local business | Paisley Scotland';
  const pageDescription = biz
    ? `${biz.business_name} in Paisley, Renfrewshire. ${biz.description ? biz.description.slice(0, 140) : `Local ${biz.category || 'business'} listed on Paisley Scotland.`}`
    : 'Local Paisley businesses directory.';

  const localBusinessSchema = biz ? {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': biz.business_name,
    'description': biz.description || `${biz.business_name} in Paisley, Renfrewshire`,
    ...(biz.address ? {
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': biz.address,
        'addressLocality': 'Paisley',
        'addressRegion': 'Renfrewshire',
        'addressCountry': 'GB',
      },
    } : {}),
    ...(biz.phone ? { 'telephone': biz.phone } : {}),
    ...(biz.email ? { 'email': biz.email } : {}),
    ...(biz.website ? { 'url': biz.website } : {}),
    ...(biz.category ? { '@type': ['LocalBusiness'], 'category': biz.category } : {}),
  } : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="directory-detail-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:site_name" content="Paisley Scotland" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {localBusinessSchema && (
          <script type="application/ld+json">
            {JSON.stringify(localBusinessSchema)}
          </script>
        )}
      </Helmet>

      <Navbar />

      <section className="pt-32 pb-12 bg-gradient-to-b from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate('/directory')}
            className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-6"
            data-testid="back-to-directory"
          >
            <ArrowLeft className="h-4 w-4" /> All Paisley businesses
          </button>
          {biz?.category && (
            <Badge className="bg-white/15 text-white border-0 mb-4">{biz.category}</Badge>
          )}
          {loading ? (
            <div className="h-12 w-2/3 bg-white/20 rounded animate-pulse" />
          ) : (
            <h1 className="font-heading text-4xl sm:text-5xl font-bold">
              {biz?.business_name || 'Business not found'}
            </h1>
          )}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          {loading ? (
            <Card><CardContent className="p-8 text-center text-slate-500">Loading business...</CardContent></Card>
          ) : error || !biz ? (
            <Card>
              <CardContent className="p-12 text-center">
                <AlertCircle className="h-10 w-10 text-slate-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Business not found</h2>
                <p className="text-slate-500 mb-6">This listing may have been removed.</p>
                <Link to="/directory">
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="browse-directory-btn">
                    Browse the directory
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 space-y-6">
                {biz.description && (
                  <p className="text-slate-700 text-lg leading-relaxed">{biz.description}</p>
                )}

                <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                  {biz.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-sky-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Address</p>
                        <p className="text-slate-700">{biz.address}</p>
                      </div>
                    </div>
                  )}
                  {biz.phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-sky-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Phone</p>
                        <a href={`tel:${biz.phone}`} className="text-slate-700 hover:text-sky-600">{biz.phone}</a>
                      </div>
                    </div>
                  )}
                  {biz.email && (
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-sky-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Email</p>
                        <a href={`mailto:${biz.email}`} className="text-slate-700 hover:text-sky-600 break-all">{biz.email}</a>
                      </div>
                    </div>
                  )}
                  {biz.website && (
                    <div className="flex items-start gap-3">
                      <Globe className="h-5 w-5 text-sky-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Website</p>
                        <a href={biz.website} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline break-all">
                          {biz.website.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 pt-6 border-t border-slate-100">
                  {biz.website && (
                    <a href={biz.website} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="visit-website-btn">
                        Visit website <Globe className="h-4 w-4 ml-2" />
                      </Button>
                    </a>
                  )}
                  <ShareMenu
                    title={biz.business_name}
                    description={biz.category ? `${biz.category} in Paisley` : 'Local Paisley business'}
                    url={pageUrl}
                    testId="share-biz-detail"
                    variant="outline"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DirectoryDetailPage;
